"use client";

import TextField from "../ui/textField";
import { TCreatePassword, TCreatePasswordPayload } from "@/interfaces";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createPasswordSchema } from "@/validations";
import { cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { useCreatePasswordMutation } from "@/hooks";
import ErrorMessage from "../ui/errorMessage";
import { useTranslations } from "next-intl";
import { FlexBetween } from "../ui";
import { useModalStore } from "@/store";
import { useSession } from "next-auth/react";
import { useToastStore } from "@/store/toastStore";

const CreatePasswordForm = () => {
  const t = useTranslations("auth");
  const { closeModal } = useModalStore();
  const { setOpenToast } = useToastStore();
  const { data: session } = useSession();
  const {
    isPending: isSending,
    mutate: createPasswordFn,
    error: createPasswordError,
  } = useCreatePasswordMutation(session?.user.token);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TCreatePassword>({
    resolver: zodResolver(createPasswordSchema),
  });
  const onSubmit = (data: TCreatePassword) => {
    const payload: Readonly<TCreatePasswordPayload> = {
      new_password: data.password,
      new_password_confirmation: data.confirmPassword,
    };

    createPasswordFn(payload, {
      onSuccess: () => {
        closeModal();
        setOpenToast({
          message: "رمز عبور با موفقیت ثبت شد",
          variant: "success",
        });
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn("mb-4 w-full")}>
      <fieldset>
        <TextField
          {...register("password")}
          placeholder={t("placeholders.newPassword")}
          type="password"
          aria-label="password"
          inputClassName="h-10"
          className={cn("mb-4")}
          value={watch("password")}
          error={errors.password?.message}
        />
      </fieldset>

      <fieldset>
        <TextField
          {...register("confirmPassword")}
          placeholder={t("placeholders.confirmPassword")}
          type="password"
          aria-label="password"
          inputClassName="h-10"
          value={watch("confirmPassword")}
          error={errors.confirmPassword?.message}
        />
      </fieldset>
      {createPasswordError && (
        <ErrorMessage message={createPasswordError.message} />
      )}
      <FlexBetween gap={4} className="mx-auto w-max">
        <CustomButton
          id="create-password-submit"
          type="submit"
          className="mt-8 w-max"
          isLoading={isSending}
        >
          {t("buttons.createPasswordSubmit")}
        </CustomButton>
      </FlexBetween>
    </form>
  );
};

CreatePasswordForm.Skeleton = function Skeleton() {
  return (
    <form className={cn("mb-4 w-full")}>
      <fieldset>
        <div
          className={cn("mb-4 h-10 w-full animate-pulse rounded bg-gray-200")}
        ></div>
      </fieldset>
      <fieldset>
        <div
          className={cn("h-10 w-full animate-pulse rounded bg-gray-200")}
        ></div>
      </fieldset>
      <div className={cn("mt-12 w-max")}>
        <div
          className={cn("h-10 w-24 animate-pulse rounded bg-gray-200")}
        ></div>
      </div>
    </form>
  );
};

export default CreatePasswordForm;
